import * as React from 'react'
import { FC, useCallback } from 'react'
import { useSelector } from 'react-redux'
import * as yup from 'yup'

import { Formik } from 'formik'
import { ISalesTeam } from '@user/store/salesTeam/typings'
import { DeleteSalesTeamFormFields } from './DeleteSalesTeamFormFields'
import { DeleteSalesTeamFormModel } from './typings'
import { selectAllSalesTeams } from '@user/store/salesTeam/slice'
import { toast } from '@sales-center/components'
import { updateDefaultSalesTeams } from '@user/store/salesTeam/api'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { useGetSalesTeamListOnMount } from '@user/store/salesTeam/hooks'
import { useGetBranchListOnMount } from '@user/store/branches/hooks/useGetBranchListOnMount'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'

interface Props {
  onClose: () => void
  onDelete: (id: string) => void
  salesTeam: ISalesTeam
}

export const DeleteSalesTeamFormWrapper: FC<Props> = (props) => {
  const { onClose, onDelete, salesTeam } = props
  const dispatch = useAppDispatch()

  // Hides company default sales team functionality
  const hideCompanyDefaultSalesTeam = true

  const salesTeamStatus = useGetSalesTeamListOnMount()
  const branchStatus = useGetBranchListOnMount()

  const salesTeams = useSelector(selectAllSalesTeams)

  const loading = salesTeamStatus == 'pending' || branchStatus == 'pending'
  const failed = salesTeamStatus == 'fail' || branchStatus == 'fail'

  const initialValues: DeleteSalesTeamFormModel = {
    branchDefault: -1,
    isBranchDefault: salesTeam.branchDefault,
    isTenantDefault: hideCompanyDefaultSalesTeam
      ? false
      : salesTeam.tenantDefault,
    tenantDefault: -1,
  }

  const validationSchema = yup.object({
    tenantDefault: yup.number().when('isTenantDefault', {
      is: true,
      then: () => yup.number().required('Please select a company default.'),
    }),
    branchDefault: yup.number().when('isBranchDefault', {
      is: true,
      then: () => yup.number().required('Please select a branch default.'),
    }),
  })

  const onSubmit = useCallback(
    async (model: DeleteSalesTeamFormModel) => {
      const allBranchDefaults = salesTeams
        .filter((s) => s.branchDefault == true)
        .map((s) => {
          return s.id
        })

      const input = {
        companyDefault:
          model.tenantDefault && model.tenantDefault != -1
            ? model.tenantDefault
            : null,
        branchDefaults:
          model.branchDefault && model.branchDefault != -1
            ? [...allBranchDefaults, model.branchDefault]
            : allBranchDefaults,
      }

      if (hideCompanyDefaultSalesTeam) {
        input.companyDefault = null
      }

      const response = await dispatch(updateDefaultSalesTeams(input))

      if (updateDefaultSalesTeams.fulfilled.match(response)) {
        toast.success('Successfully updated Sales Team defaults.')
        onDelete(salesTeam.id.toString())
        onClose()
      } else {
        toast.error('Failed to update Sales Team defaults.')
      }
    },
    [
      dispatch,
      hideCompanyDefaultSalesTeam,
      onClose,
      onDelete,
      salesTeam.id,
      salesTeams,
    ],
  )

  return (
    <>
      {loading && (
        <Box p={2} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          <DeleteSalesTeamFormFields onCancel={onClose} salesTeam={salesTeam} />
        </Formik>
      )}

      {failed && (
        <Box p={2}>
          <Alert severity="error">Failed to load Sales Team information.</Alert>
        </Box>
      )}
    </>
  )
}

DeleteSalesTeamFormWrapper.displayName = 'DeleteSalesTeamFormWrapper'
